<script>
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { NextButton },
  data() {
    return {
      step: 'password',
      currentPassword: '',
      newPassword: '',
      newPasswordConfirmation: '',
      otp: '',
      error: '',
      loading: false,
    };
  },
  computed: {
    ...mapGetters({ currentUser: 'getCurrentUser' }),
  },
  methods: {
    async submitPasswordStep() {
      this.error = '';
      if (!this.currentPassword || this.newPassword.length < 6) {
        this.error = this.$t('PROFILE_SETTINGS.FORM.ERROR');
        return;
      }
      if (this.newPassword !== this.newPasswordConfirmation) {
        this.error = this.$t(
          'ARDHISASA.AUTH.FORGOT_PASSWORD.PASSWORDS_DO_NOT_MATCH'
        );
        return;
      }
      this.loading = true;
      try {
        await this.$store.dispatch('updatePassword', {
          password: this.newPassword,
          passwordConfirmation: this.newPasswordConfirmation,
          currentPassword: this.currentPassword,
        });
        await axios.post('/api/v1/ardhisasa/auth/password_reset_request', {
          username: this.currentUser.ardhisasa_username,
        });
        this.step = 'otp';
      } catch (error) {
        this.error =
          parseAPIErrorResponse(error) ||
          this.$t('ARDHISASA.AUTH.FORGOT_PASSWORD.GENERIC_ERROR');
      } finally {
        this.loading = false;
      }
    },
    async submitOtpStep() {
      this.error = '';
      this.loading = true;
      try {
        await axios.post('/api/v1/ardhisasa/auth/password_reset_confirm', {
          username: this.currentUser.ardhisasa_username,
          otpcode: this.otp,
          new_password: this.newPassword,
          new_password_confirmation: this.newPasswordConfirmation,
        });
        await axios.post('/api/v1/profile/password_sync_complete');
        useAlert(this.$t('ARDHISASA.AUTH.PASSWORD_SYNC.SUCCESS'));
        window.location.href = `/app/accounts/${this.currentUser.account_id}/dashboard`;
      } catch {
        this.error = this.$t('ARDHISASA.AUTH.FORGOT_PASSWORD.GENERIC_ERROR');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div class="items-center bg-n-slate-2 flex justify-center h-full w-full">
    <div
      class="bg-white dark:bg-n-solid-2 rounded-xl shadow-xl w-full max-w-sm p-6"
    >
      <h2 class="text-base font-semibold text-n-slate-12 mb-1">
        {{ $t('ARDHISASA.AUTH.PASSWORD_SYNC.TITLE') }}
      </h2>
      <p class="text-xs text-n-slate-11 mb-4">
        {{ $t('ARDHISASA.AUTH.PASSWORD_SYNC.HINT') }}
      </p>

      <form
        v-if="step === 'password'"
        class="flex flex-col gap-3"
        @submit.prevent="submitPasswordStep"
      >
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ $t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.LABEL') }}
          </label>
          <input
            v-model="currentPassword"
            type="password"
            required
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ $t('PROFILE_SETTINGS.FORM.PASSWORD.LABEL') }}
          </label>
          <input
            v-model="newPassword"
            type="password"
            required
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ $t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.LABEL') }}
          </label>
          <input
            v-model="newPasswordConfirmation"
            type="password"
            required
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
          />
        </div>
        <p v-if="error" class="text-xs text-n-ruby-9">{{ error }}</p>
        <NextButton
          type="submit"
          class="w-full"
          :label="$t('ARDHISASA.AUTH.PASSWORD_SYNC.SUBMIT')"
          :disabled="loading"
          :is-loading="loading"
        />
      </form>

      <form v-else class="flex flex-col gap-3" @submit.prevent="submitOtpStep">
        <p class="text-xs text-n-slate-11">
          {{ $t('ARDHISASA.AUTH.OTP_HINT') }}
        </p>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ $t('ARDHISASA.AUTH.OTP_LABEL') }}
          </label>
          <input
            v-model="otp"
            type="text"
            inputmode="numeric"
            required
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
          />
        </div>
        <p v-if="error" class="text-xs text-n-ruby-9">{{ error }}</p>
        <NextButton
          type="submit"
          class="w-full"
          :label="$t('ARDHISASA.AUTH.VERIFY')"
          :disabled="loading"
          :is-loading="loading"
        />
      </form>
    </div>
  </div>
</template>
